import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';

import { assistantsService, type SuggestionAssistant } from '../../services/api/assistants';

/**
 * Recherche d'assistant par description du besoin.
 *
 * Avec dix-sept assistants, parcourir les vignettes ne suffit plus.
 * L'utilisateur décrit ce qu'il doit produire, on lui propose les bons.
 */
const PERTINENCE: Record<string, { fond: string; texte: string; libelle: string }> = {
  forte: { fond: 'bg-eg-valide/10', texte: 'text-eg-valide', libelle: 'correspond' },
  moyenne: { fond: 'bg-eg-alerte/10', texte: 'text-eg-alerte', libelle: 'à vérifier' },
  faible: { fond: 'bg-eg-voile', texte: 'text-eg-gris', libelle: 'éloigné' },
};

interface Props {
  /**
   * Exemple montre dans le champ. L'ecran de communication demande ce qu'on
   * veut « faire entendre ou voir » ; lui proposer un budget en exemple
   * oriente vers le mauvais assistant des la premiere seconde.
   */
  exemple?: string;
}

const EXEMPLE_PAR_DEFAUT =
  "Ex. : un budget pour l'atelier MLM de Kankan, 25 participants, cinq jours, juin 2026";

const RechercheAssistant: React.FC<Props> = ({ exemple = EXEMPLE_PAR_DEFAUT }) => {
  const navigate = useNavigate();
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionAssistant[] | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');

  const lancer = async () => {
    const demande = saisie.trim();
    if (demande.length < 3 || recherche) return;

    setRecherche(true);
    setErreur('');
    setSuggestions(null);
    setCommentaire('');
    try {
      const resultat = await assistantsService.orienter(demande);
      setSuggestions(resultat.suggestions);
      setCommentaire(resultat.commentaire || '');
    } catch (e: any) {
      setErreur(e.message || 'La recherche a échoué.');
    } finally {
      setRecherche(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-md border border-[#DDE2DD] bg-eg-blanc">
        <textarea
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            // Entrée lance la recherche ; Maj+Entrée passe à la ligne, comme
            // partout ailleurs dans la plateforme.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              lancer();
            }
          }}
          rows={3}
          placeholder={exemple}
          className="w-full resize-none border-0 bg-transparent px-3.5 pb-1.5 pt-3 text-sm
            leading-relaxed text-eg-encre placeholder:text-eg-estompe focus:outline-none
            focus:ring-0"
        />
        <div className="flex items-center justify-between gap-3 border-t border-eg-bordure px-2.5 py-2">
          <span className="pl-1 text-[11px] text-eg-gris">
            Entrée pour chercher · Maj+Entrée pour aller à la ligne
          </span>
          <button
            type="button"
            onClick={lancer}
            disabled={recherche || saisie.trim().length < 3}
            className="inline-flex items-center gap-1.5 rounded-[5px] bg-eg-ombre px-3.5 py-1.5
              text-[13px] font-semibold text-eg-blanc transition-colors hover:bg-eg-encre
              disabled:opacity-40"
          >
            {recherche && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Trouver l'assistant
            {!recherche && <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />}
          </button>
        </div>
      </div>

      {erreur && (
        <p className="rounded-md border border-eg-accent/30 bg-eg-accent/5 px-3 py-2 text-sm text-eg-accent">
          {erreur}
        </p>
      )}

      {suggestions !== null && suggestions.length > 0 && (
        <div className="space-y-1.5">
          {suggestions.map((s) => {
            const ton = PERTINENCE[s.pertinence] || PERTINENCE.faible;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => navigate(`/workspace/assistant/${s.id}`)}
                className="group flex w-full items-start gap-3 rounded-md border border-eg-bord
                  bg-eg-blanc p-3.5 text-left transition-colors hover:border-[#C9D0C9]
                  hover:bg-[#EEF2ED]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-eg-encre">{s.titre}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide
                      ${ton.fond} ${ton.texte}`}>
                      {ton.libelle}
                    </span>
                  </div>
                  <p className="mt-1 text-[13px] text-eg-gris">{s.raison}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-eg-bord group-hover:text-eg-accent" />
              </button>
            );
          })}
        </div>
      )}

      {suggestions !== null && suggestions.length === 0 && (
        <div className="rounded-md border border-eg-alerte/30 bg-eg-alerte/5 px-3.5 py-3 text-sm text-eg-encre">
          {commentaire || "Aucun assistant ne correspond à cette demande."}
        </div>
      )}
    </div>
  );
};

export default RechercheAssistant;
